<div class="quick-signup-container">
  <div class="signup-card">
    <!-- Header -->
    <div class="signup-header">
      <h2 class="signup-title">Get started with Scan Ninja</h2>
      @if (mode === 'company') {
        <p class="signup-subtitle">
          One more step: tell us your company name, then choose your plan.
        </p>
      } @else {
        <p class="signup-subtitle">
          Create your account, choose your plan, and run your first scan.
        </p>
      }
    </div>

    <!-- Main Form -->
    <div class="signup-content">
      @if (mode === 'checking') {
        <div class="text-center py-4" role="status">
          <span class="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>
          Loading your account&hellip;
        </div>
      }

      <!-- Single sign-on first (#943): no password, no verification email. -->
      @if (mode === 'signup') {
        <div class="sso-stack">
          <button type="button" appMicrosoftSignin class="btn-sso">
            <i class="fa-brands fa-microsoft" aria-hidden="true"></i>
            Continue with Microsoft
          </button>
          <button type="button" appGoogleSignin class="btn-sso">
            <i class="fa-brands fa-google" aria-hidden="true"></i>
            Continue with Google
          </button>
        </div>
        <div id="OAuthError" class="oauth-error small" role="alert"></div>

        <div class="oauth-separator" aria-hidden="true">
          <span>or sign up with your work email</span>
        </div>
      }

      <!-- Error/Success Messages -->
      @if (serverMessage) {
        <div class="alert alert-danger d-flex align-items-start gap-2" role="alert">
          <i class="fas fa-exclamation-triangle mt-1" aria-hidden="true"></i>
          <span>{{ serverMessage }}</span>
        </div>
      }

      <form [formGroup]="quickSignupForm" (ngSubmit)="onSubmit()" novalidate [hidden]="mode === 'checking'">
        @if (mode === 'signup') {
          <!-- Personal Information Row -->
          <div class="row">
            <div class="col-md-6">
              <div class="form-group mb-3">
                <label for="firstName" class="form-label">
                  First name <span class="required-mark" aria-hidden="true">*</span>
                </label>
                <input
                  type="text"
                  id="firstName"
                  class="form-control"
                  [class.is-invalid]="isFieldInvalid('firstName')"
                  formControlName="firstName"
                  placeholder="John"
                  autocomplete="given-name"
                  />
                <div class="invalid-feedback">
                  {{ getFieldError("firstName") }}
                </div>
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group mb-3">
                <label for="lastName" class="form-label">
                  Last name <span class="required-mark" aria-hidden="true">*</span>
                </label>
                <input
                  type="text"
                  id="lastName"
                  class="form-control"
                  [class.is-invalid]="isFieldInvalid('lastName')"
                  formControlName="lastName"
                  placeholder="Doe"
                  autocomplete="family-name"
                  />
                <div class="invalid-feedback">
                  {{ getFieldError("lastName") }}
                </div>
              </div>
            </div>
          </div>

          <!-- Business Email -->
          <div class="form-group mb-3">
            <label for="email" class="form-label">
              Work email <span class="required-mark" aria-hidden="true">*</span>
            </label>
            <input
              type="email"
              id="email"
              class="form-control"
              [class.is-invalid]="isFieldInvalid('email')"
              formControlName="email"
              placeholder="john@yourcompany.com"
              autocomplete="email"
              />
            <div class="invalid-feedback">
              {{ getFieldError("email") }}
            </div>
          </div>

        }

        <!-- Company Name -->
        <div class="form-group mb-3">
          <label for="companyName" class="form-label">
            Company name <span class="required-mark" aria-hidden="true">*</span>
          </label>
          <input
            type="text"
            id="companyName"
            class="form-control"
            [class.is-invalid]="isFieldInvalid('companyName')"
            formControlName="companyName"
            placeholder="Your Company Inc."
            autocomplete="organization"
            />
          <div class="invalid-feedback">
            {{ getFieldError("companyName") }}
          </div>
        </div>

        @if (mode === 'signup') {
          <!-- Password -->
          <div class="form-group mb-3">
            <label for="password" class="form-label">
              Password <span class="required-mark" aria-hidden="true">*</span>
            </label>
            <input
              type="password"
              id="password"
              class="form-control"
              [class.is-invalid]="isFieldInvalid('password')"
              formControlName="password"
              placeholder="At least 15 characters"
              autocomplete="new-password"
              aria-describedby="passwordHint"
              />
            <div class="invalid-feedback">
              {{ getFieldError("password") }}
            </div>

            <small id="passwordHint" class="form-text text-muted">
              {{ passwordHint }}
              @if (password?.value && passwordCharactersNeeded > 0) {
                <span>{{ passwordCharactersNeeded }} more to go.</span>
              }
            </small>
          </div>
        }

        <!-- Terms Agreement -->
        @if (needsTerms) {
          <div class="form-group mb-4">
            <div class="form-check">
              <input
                type="checkbox"
                id="agreeToTerms"
                class="form-check-input"
                [class.is-invalid]="isFieldInvalid('agreeToTerms')"
                formControlName="agreeToTerms"
                />
              <label for="agreeToTerms" class="form-check-label">
                I agree to the
                <a href="https://scanninja.ai/legal/terms-of-service" target="_blank"
                  rel="noopener noreferrer" class="auth-link"
                  >Terms of Service</a
                  >
                  and
                  <a href="https://scanninja.ai/legal/privacy-policy" target="_blank"
                    rel="noopener noreferrer" class="auth-link"
                    >Privacy Policy</a
                    >
                    <span class="required-mark" aria-hidden="true">*</span>
                  </label>
                  <div class="invalid-feedback">
                    {{ getFieldError("agreeToTerms") }}
                  </div>
                </div>
              </div>
            }

            <!-- Submit Button -->
            <button
              type="submit"
              class="btn btn-signup-primary w-100"
              [disabled]="loading || quickSignupForm.invalid"
              >
              @if (loading) {
                <span
                  class="spinner-border spinner-border-sm me-2"
                  aria-hidden="true"
                ></span>
              }
              @if (mode === 'company') {
                {{ loading ? "Setting up…" : "Continue to plans" }}
              } @else {
                {{ loading ? "Creating your account…" : "Create account" }}
              }
            </button>

            <!-- Benefits Preview -->
            <div class="benefits-preview mt-4">
              <h3 class="benefits-title">How it works</h3>
              <ol class="benefit-steps">
                <li class="benefit-item">
                  <span class="benefit-icon"><i class="fas fa-user-plus" aria-hidden="true"></i></span>
                  <span>Create your account</span>
                </li>
                <li class="benefit-item">
                  <span class="benefit-icon"><i class="fas fa-layer-group" aria-hidden="true"></i></span>
                  <span>Choose your plan</span>
                </li>
                <li class="benefit-item">
                  <span class="benefit-icon"><i class="fas fa-shield-alt" aria-hidden="true"></i></span>
                  <span>Run your first scan</span>
                </li>
              </ol>
            </div>
          </form>
        </div>

        <!-- Footer -->
        @if (mode === 'signup') {
          <div class="signup-footer">
            <p class="text-center">
              Already have an account?
              <a routerLink="/login" class="auth-link fw-semibold">Sign in</a>
            </p>
          </div>
        }
      </div>
    </div>
